import { useCallback, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import URLParse from 'url-parse'
import mime from 'mime'

import * as uploadService from 'services/upload'
import { trueErrorMessage } from 'utils/error'
import {
	UploadedAssetTarget,
	useCreateOrUpdateUploadedAssetMutation,
	useTargetPresignUploadUrlMutation,
} from 'graphql/generated/scheme'

/** Maximum file size for a single artifact image (5 MB) */
const ARTIFACT_MAX_SIZE_BYTES = 5 * 1024 * 1024
const ARTIFACT_MAX_SIZE_MB = 5

/** Maximum file size for a single artifact video (100 MB) — matches the API's EventArtifacts VideoMaxSize */
const ARTIFACT_MAX_VIDEO_SIZE_BYTES = 100 * 1024 * 1024
const ARTIFACT_MAX_VIDEO_SIZE_MB = 100

/** Video types the API accepts for artifact uploads */
const ARTIFACT_VIDEO_ACCEPT = 'video/mp4,video/webm'

/** Maximum number of images allowed in the editor content */
const ARTIFACT_MAX_IMAGE_COUNT = 10

/**
 * Counts the number of <img> tags currently in an HTML string.
 */
function countImagesInHtml(html: string): number {
	const matches = html.match(/<img\s/gi)
	return matches ? matches.length : 0
}

/**
 * Computes a SHA-256 hash of a Blob's contents, returned as a hex string.
 */
async function hashBlob(blob: Blob): Promise<string> {
	const buffer = await blob.arrayBuffer()
	const hashBuffer = await crypto.subtle.digest('SHA-256', buffer)
	const hashArray = Array.from(new Uint8Array(hashBuffer))
	return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
}

/**
 * Hook that provides a TinyMCE-compatible `images_upload_handler` which
 * uploads images to S3 via presigned URLs and returns the CDN URL.
 *
 * Deduplicates uploads by hashing image content — identical images
 * resolve immediately with the cached CDN URL.
 *
 * Also exposes `isUploading` so consumers can disable save/submit buttons
 * until all uploads complete.
 */
export function useArtifactUpload(onUploadingChange?: (isUploading: boolean) => void) {
	const [presignUploadUrl] = useTargetPresignUploadUrlMutation()
	const [createOrUpdateUploadedAsset] = useCreateOrUpdateUploadedAssetMutation()

	// Track number of in-flight uploads so we know when all are done
	const pendingCountRef = useRef(0)
	const [isUploading, setIsUploading] = useState(false)

	// Reported straight from the upload's start and settle (not from an effect), so an upload that outlives its
	// component — an editor removed mid-upload — still reports "done" when it settles.
	const onUploadingChangeRef = useRef(onUploadingChange)
	onUploadingChangeRef.current = onUploadingChange

	// Cache of hash → CDN URL for deduplication
	const uploadCacheRef = useRef<Map<string, string>>(new Map())

	const incrementPending = useCallback(() => {
		pendingCountRef.current += 1
		setIsUploading(true)
		if (pendingCountRef.current === 1) onUploadingChangeRef.current?.(true)
	}, [])

	const decrementPending = useCallback(() => {
		pendingCountRef.current = Math.max(0, pendingCountRef.current - 1)
		if (pendingCountRef.current === 0) {
			setIsUploading(false)
			onUploadingChangeRef.current?.(false)
		}
	}, [])

	// Keep a ref to the current editor content getter so the handler can
	// check the image count at upload time without stale closures.
	const getContentRef = useRef<() => string>(() => '')

	const setContentGetter = useCallback((getter: () => string) => {
		getContentRef.current = getter
	}, [])

	/**
	 * Uploads a blob to S3 via a presigned URL, records it as an uploaded asset,
	 * and returns its CDN URL.
	 */
	const uploadArtifact = useCallback(
		async (
			blob: Blob,
			contentType: string,
			extension: string,
			onProgress?: (percent: number) => void
		): Promise<string> => {
			const { data } = await presignUploadUrl({
				variables: {
					contentType,
					extension,
					contentLength: blob.size,
					target: UploadedAssetTarget.EventArtifact,
				},
			})

			const presignedUrl = data?.uploaded_asset?.presignUploadUrl
			if (!presignedUrl) {
				throw new Error('Failed to get upload URL')
			}

			await uploadService.uploadToPresignedUrl({
				presignedS3Url: presignedUrl,
				file: blob,
				onProgress,
			})

			// Extract the relative path from the presigned URL
			const relativePath = URLParse(presignedUrl, true).pathname.slice(1)

			// Record the uploaded asset — awaited so an untracked upload is reported as failed
			await createOrUpdateUploadedAsset({
				variables: {
					url: relativePath,
					target: UploadedAssetTarget.EventArtifact,
				},
			})

			return `${import.meta.env.VITE_MEDIA_URL}/${relativePath}`
		},
		[presignUploadUrl, createOrUpdateUploadedAsset]
	)

	/**
	 * TinyMCE images_upload_handler callback.
	 * Receives a BlobInfo object and returns a Promise<string> with the image URL.
	 */
	const imagesUploadHandler = useCallback(
		async (blobInfo: { blob: () => Blob; filename: () => string }): Promise<string> => {
			const blob = blobInfo.blob()
			const filename = blobInfo.filename()

			// --- Validate file size ---
			if (blob.size > ARTIFACT_MAX_SIZE_BYTES) {
				const msg = `Image exceeds the ${ARTIFACT_MAX_SIZE_MB}MB size limit. Please use a smaller image.`
				toast.error(msg)
				throw new Error(msg)
			}

			// --- Validate image count ---
			const currentContent = getContentRef.current()
			const currentCount = countImagesInHtml(currentContent)
			if (currentCount >= ARTIFACT_MAX_IMAGE_COUNT) {
				const msg = `Maximum of ${ARTIFACT_MAX_IMAGE_COUNT} images allowed per description. Please remove an image before adding another.`
				toast.error(msg)
				throw new Error(msg)
			}

			// --- Check for duplicate via content hash ---
			const hash = await hashBlob(blob)
			const cachedUrl = uploadCacheRef.current.get(hash)
			if (cachedUrl) {
				return cachedUrl
			}

			// --- Determine content type and extension ---
			const contentType = blob.type || 'image/png'
			const extension = mime.getExtension(contentType) || filename.split('.').pop() || 'png'

			// --- Track upload in progress ---
			incrementPending()

			try {
				const cdnUrl = await uploadArtifact(blob, contentType, extension)
				uploadCacheRef.current.set(hash, cdnUrl)
				return cdnUrl
			} catch (error) {
				const msg = trueErrorMessage(error)
				toast.error(`Image upload failed: ${msg}`)
				throw new Error(msg)
			} finally {
				decrementPending()
			}
		},
		[uploadArtifact, incrementPending, decrementPending]
	)

	/**
	 * Uploads a video picked in the "Add video" dialog and returns its CDN URL. Rejects with a user-facing
	 * message (the dialog shows it) when the file is the wrong type or too large, or the upload fails.
	 */
	const uploadVideo = useCallback(
		async (file: File, onProgress?: (percent: number) => void): Promise<string> => {
			const extension = mime.getExtension(file.type)
			if (!extension || !ARTIFACT_VIDEO_ACCEPT.split(',').includes(file.type)) {
				throw new Error('Only MP4 or WebM videos can be uploaded.')
			}
			if (file.size > ARTIFACT_MAX_VIDEO_SIZE_BYTES) {
				throw new Error(`Video exceeds the ${ARTIFACT_MAX_VIDEO_SIZE_MB}MB size limit. Please use a smaller video.`)
			}

			incrementPending()
			try {
				return await uploadArtifact(file, file.type, extension, onProgress)
			} catch (error) {
				throw new Error(`Upload failed: ${trueErrorMessage(error)}`)
			} finally {
				decrementPending()
			}
		},
		[uploadArtifact, incrementPending, decrementPending]
	)

	return {
		imagesUploadHandler,
		uploadVideo,
		setContentGetter,
		isUploading,
		maxImageCount: ARTIFACT_MAX_IMAGE_COUNT,
		maxSizeMb: ARTIFACT_MAX_SIZE_MB,
		maxVideoSizeMb: ARTIFACT_MAX_VIDEO_SIZE_MB,
		videoAccept: ARTIFACT_VIDEO_ACCEPT,
	}
}
